// O EXPLORAR NOVO (repaginação de 30/10/2026): a vitrine da curadoria.
// A Temporada 1 como coleção nomeada (o lançamento em destaque), os livros por
// OBJETIVO, o que vem em breve (com o voto "quero esta travessia"), a MINHA
// LISTA com o calendário e, abaixo da curadoria, o acervo inteiro.
// A ficha de cada livro sai do mapa da Travessia: o que a pessoa leva, para
// quem é, o caminho em etapas. Nunca reconta o livro nem mostra o texto dele.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BookV2 } from '../../types';
import { api, type Curadoria, type CuradoriaFicha, type CuradoriaLivro } from '../../services/apiV2';
import { AcervoMosaico, AcervoLivroScreen, type LivroAcervoCompleto } from '../acervo';
import { openExternalUrl } from '../platform';
import { toast } from '../toast';
import { Icon } from '../icons';
import { NvCapa } from './home-nova';
import { CardPro } from './pro';
import './nova.css';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const chaveMes = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const nomeMes = (chave: string) => { const [a, m] = chave.split('-').map(Number); return `${MESES[m - 1]}${a !== new Date().getFullYear() ? ` de ${a}` : ''}`; };
const proximosMeses = (n: number) => { const d = new Date(); d.setDate(1); return Array.from({ length: n }, (_, i) => chaveMes(new Date(d.getFullYear(), d.getMonth() + i, 1))); };
const absoluta = (u: string | null) => (u ? new URL(u, window.location.origin).href : null);

type Comecar = (slug: string, acesso: boolean) => Promise<void>;

export const ExplorarNova: React.FC<{
  session: any;
  books: BookV2[];
  onEstanteMudou: () => Promise<any> | void;
  onComecar: Comecar;
  onAbrirLivro: (b: BookV2) => void;
  /** As trilhas (refino de 30/10/2026): esta conta abre as travessias? É Pro? */
  acessoTrilhas?: boolean;
  pro?: boolean;
  onPro?: () => void;
}> = ({ session, books, onEstanteMudou, onComecar, onAbrirLivro, pro = true, onPro }) => {
  const [cur, setCur] = useState<Curadoria | null>(null);
  const [erro, setErro] = useState(false);
  const [votos, setVotos] = useState<Set<string>>(new Set());
  const [objetivo, setObjetivo] = useState<string | null>(null);
  const [ficha, setFicha] = useState<string | null>(null);
  const [acervoSlug, setAcervoSlug] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const acervoRef = useRef<HTMLDivElement | null>(null);
  const rolagem = useRef(0);

  const carregar = useCallback(() => {
    api.curadoria(session)
      .then((c) => { setCur(c); setVotos(new Set(c.votos)); setObjetivo((o) => o || c.objetivos[0]?.id || null); setErro(false); })
      .catch(() => setErro(true));
  }, [session]);
  useEffect(() => { carregar(); }, [carregar]);

  const curados = useMemo(() => new Set([...(cur?.temporada.livros || []), ...(cur?.objetivos || []).flatMap((o) => o.livros)].map((l) => l.slug)), [cur]);

  const votar = async (slug: string) => {
    const quero = !votos.has(slug);
    setVotos((v) => { const n = new Set(v); quero ? n.add(slug) : n.delete(slug); return n; });
    // Sem toast de sucesso: o botão muda (✓) e o toast cobria o Voltar da ficha.
    try { await api.votarTravessia(session, slug, quero); }
    catch { setVotos((v) => { const n = new Set(v); quero ? n.delete(slug) : n.add(slug); return n; }); toast.error('Não deu para guardar o voto agora'); }
  };
  const abrirFicha = (slug: string) => { rolagem.current = window.scrollY; setFicha(slug); window.scrollTo(0, 0); };
  const fechar = () => { setFicha(null); setAcervoSlug(null); requestAnimationFrame(() => window.scrollTo(0, rolagem.current)); };
  const abrirDoAcervo = (slug: string) => {
    rolagem.current = window.scrollY;
    if (curados.has(slug)) setFicha(slug); else setAcervoSlug(slug);
    window.scrollTo(0, 0);
  };

  // Do acervo (fora da curadoria): o mesmo "quero ler" de antes.
  const querLerDoAcervo = async (livro: LivroAcervoCompleto) => {
    try {
      await api.createBook(session, {
        title: livro.titulo, author: livro.autor || '', totalPages: livro.paginas || 0, status: 'wishlist',
        coverSourceUrl: absoluta(livro.capa), isbn: livro.isbn13 || null,
      });
      await onEstanteMudou();
      toast.success('Está na sua lista de leituras');
      fechar();
    } catch (e: any) {
      const m = String(e?.message || '');
      if (e?.status === 400 && m && !/^HTTP /.test(m)) toast.error(m); else toast.error('Não deu para guardar agora');
    }
  };

  if (ficha) {
    return <FichaLivro session={session} slug={ficha} votou={votos.has(ficha)} onVotar={() => votar(ficha)} onVoltar={fechar}
      onEstanteMudou={async () => { await onEstanteMudou(); carregar(); }} onComecar={onComecar} onPro={onPro} />;
  }
  if (acervoSlug) {
    return <AcervoLivroScreen slug={acervoSlug} session={session} onVoltar={fechar} onAdicionarNaEstante={querLerDoAcervo} />;
  }

  const t = cur?.temporada;
  const lancamento = t?.livros.find((l) => l.travessia === 'disponivel') || null;
  const emBreve = (t?.livros || []).filter((l) => l.travessia === 'em_breve');
  const abertas = (t?.livros || []).filter((l) => l.travessia === 'disponivel').length;
  const obj = cur?.objetivos.find((o) => o.id === objetivo) || null;

  return (
    <div className="nv" data-tela="explorar-nova">
      <div className="nv-topo nv-entra"><span className="nv-mono">Explorar · curadoria</span></div>
      <h1 className="nv-titulo-tela nv-serif nv-entra">Livros para <em>ler e aplicar.</em></h1>
      <p className="nv-sub nv-entra nv-entra-2">Poucos, escolhidos a dedo. Cada um com a sua trilha: o Mentor acompanha a leitura e os desafios põem a ideia em prática.</p>
      <label className="nv-busca nv-entra nv-entra-2">
        <Icon name="search" size={17} />
        <input value={busca} placeholder="Buscar no acervo" aria-label="Buscar no acervo"
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') acervoRef.current?.scrollIntoView({ behavior: 'smooth' }); }} />
      </label>

      {erro && <p className="nv-vazio">Não deu para carregar a curadoria agora. <button className="nv-link" onClick={carregar}>Tentar de novo</button></p>}

      {!busca && t && (
        <>
          <section className="nv-secao" data-temporada={t.n}>
            <span className="nv-mono" style={{ color: 'var(--ember)' }}>{t.nome}</span>
            <h2 className="nv-serif">{t.titulo}</h2>
            <p className="nv-sub">{t.frase}</p>
            <div className="nv-contagem nv-mono" data-contagem-trilhas>
              <b>{abertas === 1 ? '1 trilha aberta' : `${abertas} trilhas abertas`}</b> · {emBreve.length} a caminho
            </div>
            {lancamento && (
              <button className="nv-lancamento nv-entra" onClick={() => abrirFicha(lancamento.slug)} data-lancamento={lancamento.slug}>
                <NvCapa src={lancamento.capa} titulo={lancamento.titulo} w={116} h={174} />
                <div>
                  <span className="nv-selo disponivel nv-mono"><i />Trilha aberta</span>
                  <h3 className="nv-serif">{lancamento.titulo}</h3>
                  {lancamento.habilidade && <span className="nv-mono" style={{ color: 'var(--ember)', display: 'block', marginBottom: 6 }}>Você aprende · {lancamento.habilidade}</span>}
                  <p>{lancamento.nota}</p>
                  <div className="nv-lancamento-pe nv-mono">{lancamento.etapas} etapas · {lancamento.paginas} p. · ver a trilha →</div>
                </div>
              </button>
            )}

            <div className="nv-oque" data-oque-trilha>
              <span className="nv-mono">O que é uma trilha</span>
              <h3>Você lê o livro. A trilha te faz <em>usar</em> o que leu.</h3>
              <ol>
                <li>Tenha o livro: na Amazon ou o seu exemplar.</li>
                <li>Registre a página: o Mentor comenta o trecho.</li>
                <li>Faça o desafio e aplique no mesmo dia.</li>
              </ol>
            </div>

            <div className="nv-temp" data-grade-temporada data-em-breve>
              {t.livros.map((l) => (
                <div key={l.slug} className="nv-temp-item" data-travessia={l.travessia}>
                  <button onClick={() => abrirFicha(l.slug)} aria-label={`Ver a trilha de ${l.titulo}`} tabIndex={-1}><NvCapa src={l.capa} titulo={l.titulo} w={58} h={87} /></button>
                  <div style={{ minWidth: 0 }}>
                    <button className="nv-temp-corpo" onClick={() => abrirFicha(l.slug)} data-livro-temporada={l.slug} data-travessia={l.travessia}>
                      {l.habilidade && <span className="nv-mono">Você aprende · {l.habilidade}</span>}
                      <b>{l.titulo}</b>
                      <p>{l.nota}</p>
                    </button>
                    <div className="nv-temp-pe">
                      <span className={`nv-selo nv-mono ${l.travessia === 'disponivel' ? 'disponivel' : ''}`}>{l.travessia === 'disponivel' ? <><i />Trilha aberta</> : 'Em breve'}</span>
                      {l.travessia === 'em_breve' && (
                        <button className="nv-voto" aria-pressed={votos.has(l.slug)} onClick={() => votar(l.slug)} data-votar={l.slug}>
                          {votos.has(l.slug) ? 'Quero ✓' : 'Quero esta'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="nv-renova nv-mono">{t.renovacao}{emBreve.length ? ' Vote na que você quer primeiro: a mais pedida sai antes.' : ''}</div>
            {!pro && onPro && <CardPro onAbrir={onPro} onde="explorar" capas={t.livros.map((l) => l.capa)} />}
          </section>

          {!!cur?.biografias?.livros.length && (
            <section className="nv-secao" data-biografias>
              <span className="nv-mono" style={{ color: 'var(--ember)' }}>{cur.biografias.nome}</span>
              <h2 className="nv-serif">{cur.biografias.titulo}</h2>
              <p className="nv-sub">{cur.biografias.frase}</p>
              <div className="nv-temp">
                {cur.biografias.livros.map((l) => (
                  <div key={l.slug} className="nv-temp-item" data-travessia={l.travessia}>
                    <button onClick={() => abrirFicha(l.slug)} aria-label={`Ver a trilha de ${l.titulo}`} tabIndex={-1}><NvCapa src={l.capa} titulo={l.titulo} w={58} h={87} /></button>
                    <div style={{ minWidth: 0 }}>
                      <button className="nv-temp-corpo" onClick={() => abrirFicha(l.slug)} data-livro-biografia={l.slug} data-travessia={l.travessia}>
                        {l.habilidade && <span className="nv-mono">Você aprende · {l.habilidade}</span>}
                        <b>{l.titulo}</b>
                        <p>{l.nota}</p>
                      </button>
                      <div className="nv-temp-pe">
                        <span className={`nv-selo nv-mono ${l.travessia === 'disponivel' ? 'disponivel' : ''}`}>{l.travessia === 'disponivel' ? <><i />Trilha aberta</> : 'Em breve'}</span>
                        {l.travessia === 'disponivel' && l.etapas && <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>{l.etapas} etapas · {l.paginas} p.</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="nv-secao" data-objetivos>
            <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>Por objetivo</span>
            <h2 className="nv-serif">O que você quer <em>mudar?</em></h2>
            <div className="nv-chips">
              {cur!.objetivos.map((o) => (
                <button key={o.id} className="nv-chip" aria-pressed={o.id === objetivo} onClick={() => setObjetivo(o.id)} data-objetivo={o.id}>{o.nome}</button>
              ))}
            </div>
            {obj && (
              <>
                <p className="nv-objetivo-frase nv-serif">{obj.frase}</p>
                <div className="nv-fileira" style={{ marginTop: 14 }}>
                  {obj.livros.map((l) => (
                    <button key={l.slug} className="nv-mini" onClick={() => abrirFicha(l.slug)} data-livro-objetivo={l.slug}>
                      <NvCapa src={l.capa} titulo={l.titulo} w={74} h={111} />
                      <span className="nv-mini-pct nv-mono">{l.travessia === 'disponivel' ? 'Travessia' : l.temporada ? 'Em breve' : 'Acervo'}</span>
                      <span className="nv-mini-titulo">{l.titulo}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>

        </>
      )}

      {!busca && <MinhaLista session={session} books={books} onAbrirLivro={onAbrirLivro} />}

      <section className="nv-secao" ref={acervoRef} data-acervo>
        <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>O acervo</span>
        <h2 className="nv-serif">{busca ? <>Resultado <em>no acervo</em></> : <>Todos os <em>livros</em></>}</h2>
        {!busca && <p className="nv-sub" data-clareza="ideias">Ideias para ler no seu exemplar. A travessia é dos livros da temporada.</p>}
        {/* o mosaico tem recuo próprio de 18 px; a tela nova já tem 22 */}
        <div style={{ margin: '12px -18px 0' }}>
          <AcervoMosaico onAbrir={abrirDoAcervo} busca={busca} naCuradoria />
        </div>
      </section>
    </div>
  );
};

/** MINHA LISTA DE LEITURAS + o calendário: em que mês ler cada uma. */
const MinhaLista: React.FC<{ session: any; books: BookV2[]; onAbrirLivro: (b: BookV2) => void }> = ({ session, books, onAbrirLivro }) => {
  const lista = useMemo(() => books.filter((b) => b.status === 'wishlist'), [books]);
  const [meses, setMeses] = useState<Record<string, string>>({});
  useEffect(() => {
    let vivo = true;
    api.calendarioLista(session).then((r) => { if (vivo) setMeses(Object.fromEntries(r.itens.map((i) => [i.book_id, i.mes]))); }).catch(() => {});
    return () => { vivo = false; };
  }, [session]);
  const opcoes = proximosMeses(12);
  const agora = opcoes[0];

  const marcar = async (b: BookV2, mes: string) => {
    const antes = meses[b.id];
    setMeses((m) => { const n = { ...m }; if (mes) n[b.id] = mes; else delete n[b.id]; return n; });
    try { await api.marcarMesLista(session, b.id, mes || null); }
    catch { setMeses((m) => ({ ...m, [b.id]: antes })); toast.error('Não deu para guardar o mês agora'); }
  };

  // Grupos: os meses marcados em ordem (o que já passou entra em "este mês") e, por fim, sem data.
  const grupos = useMemo(() => {
    const porMes: Record<string, BookV2[]> = {};
    const semData: BookV2[] = [];
    for (const b of lista) {
      const m = meses[b.id];
      if (!m) { semData.push(b); continue; }
      const k = m < agora ? agora : m;
      (porMes[k] = porMes[k] || []).push(b);
    }
    const ordem = Object.keys(porMes).sort();
    const rotulo = (k: string) => (k === agora ? `Este mês · ${nomeMes(k)}` : k === opcoes[1] ? `Mês que vem · ${nomeMes(k)}` : nomeMes(k));
    return [...ordem.map((k) => ({ k, rotulo: rotulo(k), livros: porMes[k] })), ...(semData.length ? [{ k: 'sem', rotulo: 'Sem data', livros: semData }] : [])];
  }, [lista, meses, agora, opcoes]);

  return (
    <section className="nv-secao" data-minha-lista={lista.length}>
      <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>Minha lista de leituras</span>
      <h2 className="nv-serif">O que vem <em>depois</em></h2>
      {lista.length === 0 ? (
        <p className="nv-vazio">Sua lista está vazia. Abra um livro da temporada ou do acervo e toque em “Pôr na minha lista”; aqui você escolhe o mês de cada um.</p>
      ) : grupos.map((g) => (
        <div key={g.k} className="nv-lista-mes" data-grupo-mes={g.k}>
          <span className="nv-mono">{g.rotulo}</span>
          {g.livros.map((b) => (
            <div key={b.id} className="nv-lista-item">
              <NvCapa book={b} w={40} h={60} />
              <button onClick={() => onAbrirLivro(b)}><b>{b.title}</b><small>{b.author}{b.pages ? ` · ${b.pages} p.` : ''}</small></button>
              <select className="nv-mes" value={meses[b.id] && meses[b.id] >= agora ? meses[b.id] : (meses[b.id] ? agora : '')}
                onChange={(e) => marcar(b, e.target.value)} aria-label={`Mês para ler ${b.title}`} data-mes-livro={b.id}>
                <option value="">Sem data</option>
                {opcoes.map((k) => <option key={k} value={k}>{nomeMes(k).slice(0, 3)}{k.slice(0, 4) !== agora.slice(0, 4) ? ` ${k.slice(2, 4)}` : ''}</option>)}
              </select>
            </div>
          ))}
        </div>
      ))}
    </section>
  );
};

/** A FICHA de um livro da curadoria. */
export const FichaLivro: React.FC<{
  session: any; slug: string; votou: boolean;
  onVotar: () => void; onVoltar: () => void; onEstanteMudou: () => Promise<any>; onComecar: Comecar;
  /** Sem acesso à travessia, a ficha leva à vitrine do Pro. */
  onPro?: () => void;
}> = ({ session, slug, votou, onVotar, onVoltar, onEstanteMudou, onComecar, onPro }) => {
  const [d, setD] = useState<CuradoriaFicha | null>(null);
  const [erro, setErro] = useState(false);
  const [ocupado, setOcupado] = useState<null | 'lista' | 'comecar'>(null);
  const carregar = useCallback(() => {
    api.curadoriaFicha(session, slug).then((r) => { setD(r); setErro(false); }).catch(() => setErro(true));
  }, [session, slug]);
  useEffect(() => { carregar(); }, [carregar]);
  // A ficha é tela de detalhe: a barra de abas sai para a barra de ações
  // (Começar · Comprar · Pôr na lista) não ficar escondida atrás dela.
  useEffect(() => {
    document.body.classList.add('nv-ficha-aberta');
    return () => document.body.classList.remove('nv-ficha-aberta');
  }, []);

  if (erro) return <div className="nv nv-ficha"><button className="nv-voltar nv-mono" onClick={onVoltar}><Icon name="chevron-left" size={16} />Voltar</button><p className="nv-vazio">Não deu para abrir esta ficha agora.</p></div>;
  if (!d) return <div className="nv nv-ficha" data-tela="ficha-carregando"><button className="nv-voltar nv-mono" onClick={onVoltar}><Icon name="chevron-left" size={16} />Voltar</button></div>;

  const l: CuradoriaLivro = d.livro;
  const f = d.ficha;
  const meu = d.meu_livro;
  const temTravessia = l.travessia === 'disponivel' && !!f;

  const porNaLista = async () => {
    if (ocupado || meu) return;
    setOcupado('lista');
    try {
      await api.createBook(session, { title: l.titulo, author: l.autor || '', totalPages: l.paginas || 0, status: 'wishlist', coverSourceUrl: absoluta(l.capa) });
      await onEstanteMudou();
      carregar();
    } catch { toast.error('Não deu para guardar agora'); }
    finally { setOcupado(null); }
  };
  // Sem o Pro, a travessia fica trancada — a vitrine explica. Quem não quer
  // a trilha começa a ler do mesmo jeito (o livro entra em leitura, sem o mapa).
  // A 1ª etapa grátis (31/10/2026): com a interface nova e sem o Pro, a 1ª
  // etapa da trilha abre por inteiro; as outras seguem com o Pro.
  const travada = temTravessia && !d.acesso_travessia && !!onPro;
  const etapa1 = travada && !!d.etapa1_gratis;
  const comecar = async (comTrilha = true) => {
    if (ocupado) return;
    setOcupado('comecar');
    try { await onComecar(slug, comTrilha && (d.acesso_travessia || etapa1)); }
    catch { toast.error('Não deu para começar agora'); }
    finally { setOcupado(null); }
  };

  const rotuloComecar = !meu ? 'Começar a travessia'
    : meu.status === 'lendo' ? 'Continuar a travessia'
    : meu.status === 'lido' ? 'Ver a travessia' : 'Começar a travessia';

  return (
    <div className="nv nv-ficha" data-tela="ficha" data-ficha={slug}>
      <button className="nv-voltar nv-mono" onClick={onVoltar} data-voltar><Icon name="chevron-left" size={16} />Explorar</button>
      <div className="nv-ficha-capa nv-entra">
        <NvCapa src={l.capa} titulo={l.titulo} w={150} h={225} />
        <h1 className="nv-serif">{l.titulo}</h1>
        <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>{l.autor}{l.paginas ? ` · ${l.paginas} p.` : ''}</span>
        {l.habilidade && <div className="nv-habilidade nv-mono" data-habilidade>Você aprende · {l.habilidade}</div>}
        <div style={{ marginTop: 14 }}>
          <span className={`nv-selo nv-mono ${temTravessia ? 'disponivel' : ''}`}>{temTravessia ? <><i />Trilha aberta · {f!.etapas.length} etapas{travada ? ' · Pro' : ''}</> : l.temporada ? 'Trilha em breve' : 'Fora da temporada atual'}</span>
        </div>
      </div>

      {f ? (
        <>
          <p className="nv-gancho nv-serif nv-entra nv-entra-2">{f.gancho}</p>
          <div className="nv-bloco" data-bloco="resultados"><span className="nv-mono">O que você vai conseguir</span><ul>{f.resultados.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          <div className="nv-dois">
            <div className="nv-bloco" data-bloco="para-quem"><span className="nv-mono">Para quem é</span><ul>{f.para_quem.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
            <div className="nv-bloco nao" data-bloco="nao-para-quem"><span className="nv-mono" style={{ color: 'var(--ink-3)' }}>Para quem não é</span><ul>{f.nao_para_quem.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          </div>
          <div className="nv-bloco" data-bloco="na-trilha"><span className="nv-mono">O que vem na travessia</span><ul>{f.na_trilha.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
          <div className="nv-amostra" data-bloco="amostra">
            <span className="nv-mono">Desafio de amostra</span>
            <h3 className="nv-serif">{f.desafio_exemplo.titulo}</h3>
            <p>{f.desafio_exemplo.corpo}</p>
          </div>
          <div className="nv-bloco" data-bloco="caminho">
            <span className="nv-mono">O caminho · {f.etapas.length} etapas</span>
            <div className="nv-etapas">
              {f.etapas.map((e) => (
                <div key={e.n} className="nv-etapa"><span className="nv-mono">Etapa {e.n} · p. {e.de}–{e.ate}</span><b>{e.nome}</b><p>{e.resumo}</p></div>
              ))}
            </div>
          </div>
          <div className="nv-tempo" data-bloco="tempo">
            <div><span className="nv-mono">Tempo</span><p>{f.tempo}</p></div>
            <div><span className="nv-mono">Esforço</span><p>{f.esforco}</p></div>
          </div>
          {travada && (
            <div className="nv-ficha-pro" data-ficha-pro>
              <span className="nv-mono">Incluída no Forjamente Pro</span>
              {etapa1 ? (
                <>
                  <h3>A 1ª etapa é grátis.</h3>
                  <p>Faça a etapa “{f.etapas[0]?.nome}” inteira, com os comentários e os desafios do Mentor. As outras {f.etapas.length - 1} abrem com o Pro. Registro, notas e clubes continuam grátis.</p>
                  <button className="nv-primario" style={{ marginTop: 6 }} onClick={() => comecar(true)} disabled={!!ocupado} data-etapa1-gratis>
                    {ocupado === 'comecar' ? 'Abrindo…' : meu && meu.status !== 'proximo' ? 'Continuar a 1ª etapa grátis' : 'Fazer a 1ª etapa grátis'}
                  </button>
                </>
              ) : (
                <>
                  <h3>Esta trilha abre com o Pro.</h3>
                  <p>O Mentor acompanha a sua leitura e as {f.etapas.length} etapas se abrem conforme você avança, com os desafios de cada uma. Registro, notas e clubes continuam grátis.</p>
                </>
              )}
              <button className="nv-secundario" onClick={onPro} data-ficha-ver-pro>Ver o que o Pro inclui</button>
              <button className="nv-link" onClick={() => comecar(false)} disabled={!!ocupado} data-ler-sem-trilha>ou comece a ler sem a trilha</button>
            </div>
          )}
        </>
      ) : (
        <>
          {l.nota && <p className="nv-gancho nv-serif nv-entra nv-entra-2">{l.nota}</p>}
          <div className="nv-embreve-ficha" data-travessia-em-breve>
            <span className="nv-mono" style={{ color: 'var(--ember)' }}>Travessia em breve</span>
            <h3 className="nv-serif">Este caminho ainda está sendo traçado.</h3>
            <p>As travessias saem pela ordem dos votos. Quer esta? Avisamos quando abrir{onPro ? ' — e ela entra no Pro' : ''}.</p>
            <button className="nv-voto" aria-pressed={votou} onClick={onVotar} data-votar={slug}>{votou ? 'Você quer esta travessia ✓' : 'Quero esta travessia'}</button>
          </div>
        </>
      )}

      <div className="nv-barra-acoes" data-acoes-ficha>
        {temTravessia && (travada ? (
          <button className="nv-primario" onClick={onPro} data-liberar-pro>
            <Icon name="sparkle" size={17} /> Liberar a trilha · Pro
          </button>
        ) : (
          <button className="nv-primario" onClick={() => comecar()} disabled={!!ocupado} data-comecar>
            {ocupado === 'comecar' ? 'Abrindo…' : d.acesso_travessia ? rotuloComecar : 'Começar a ler'}
          </button>
        ))}
        <div className="nv-linha">
          <button className="nv-secundario" onClick={() => l.compra && openExternalUrl(l.compra)} disabled={!l.compra} data-comprar>Comprar na Amazon</button>
          <button className="nv-secundario" onClick={porNaLista} disabled={!!ocupado || !!meu} data-por-na-lista>
            {meu ? (meu.status === 'proximo' ? 'Na sua lista ✓' : meu.status === 'lido' ? 'Já lido ✓' : 'Na estante ✓') : ocupado === 'lista' ? 'Guardando…' : 'Pôr na minha lista'}
          </button>
        </div>
      </div>
    </div>
  );
};
